/**
 * Timeline layout configuration and constants
 */

export const TIMELINE_LAYOUT = {
    // Header widths
    HEADER_WIDTH_MOBILE: 60,
    HEADER_WIDTH_DESKTOP: 232,
    
    // Axis dimensions
    AXIS_HEIGHT: 56, // h-14 = 3.5rem = 56px
    
    // Spacing
    VERTICAL_TICK_BOTTOM_OFFSET: 96, // bottom-24 = 6rem = 96px
    PASSAGE_DATE_BOTTOM_OFFSET: 0,
    
    // Zoom limits
    MIN_ZOOM: 0.1, // Entire history fits on screen
    MAX_ZOOM: 200, // Maximum pixels per year
    DEFAULT_ZOOM: 5,
    
    // Ancient World Timeline defaults
    ANCIENT_WORLD_DEFAULT_ZOOM: 2.0, // Show ~600 years on typical screen (more zoomed in)
    ANCIENT_WORLD_DEFAULT_CENTER_YEAR: -700, // 700 BC - peak of prophetic activity
    
    // Performance
    VIRTUALIZATION_BUFFER_MULTIPLIER: 2, // 2x viewport width on each side
    
    // Initial view
    PASSAGE_PADDING_MULTIPLIER: 1.8 // 40% padding on each side = 1.8x total
} as const;

/**
 * Get the appropriate header width based on current screen size
 */
export function getHeaderWidth(): number {
    if (typeof window === 'undefined') {
        return TIMELINE_LAYOUT.HEADER_WIDTH_DESKTOP;
    }
    return window.innerWidth >= 640
        ? TIMELINE_LAYOUT.HEADER_WIDTH_DESKTOP
        : TIMELINE_LAYOUT.HEADER_WIDTH_MOBILE;
}

/**
 * Calculate optimal zoom level for a given passage duration
 */
export function calculateOptimalZoom(
    passageStartYear: number,
    passageEndYear: number | null,
    containerWidth: number
): number {
    const actualEndYear = passageEndYear || passageStartYear;
    
    // Calculate duration, defaulting to at least 5 years for single-point events
    const duration = Math.max(5, actualEndYear - passageStartYear);
    
    // Add padding
    const totalYearsToShow = duration * TIMELINE_LAYOUT.PASSAGE_PADDING_MULTIPLIER;
    
    // Subtract header width from available space
    const availableWidth = containerWidth - getHeaderWidth();
    
    const calculatedZoom = availableWidth / totalYearsToShow;
    
    // Clamp between min and max zoom
    return Math.min(
        TIMELINE_LAYOUT.MAX_ZOOM,
        Math.max(TIMELINE_LAYOUT.MIN_ZOOM, calculatedZoom)
    );
}

